import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { debounce } from "capitalrx-components-2.0";
import { Dropdown, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Icon, LoadingGroup, LoadingSkeleton } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Paragraph, Subtitle } from "capitalrx-components-2.0/lib/typography";
import { CaseListResult } from "capitalrx-types";

import { useListMemberDependentsQuery } from "@/App/api/member";
import { useListPriorAuthorizationCaseQuery } from "@/App/api/priorAuthorization";
import { ContentPanel, NoResults, PageWrapper, Pager, DependentDropdown } from "@/App/components";
import { ExperienceContext, MemberEligibilityContext } from "@/App/context";
import { useNavigateClient } from "@/App/hooks";
import { PriorAuthorizationCard } from "@/App/pages/PriorAuthorization/PriorAuthorizationCard";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";

const RESULTS_PER_PAGE = 6;

const TRANSLATION_PATH = "PRIOR_AUTHORIZATION.priorAuthorizationListPage";

const changeSearch = debounce((setter: (v: string) => void, value: string) => {
  if (value && value.length > 3) {
    logEvent(ANALYTIC_EVENTS.PRIOR_AUTHORIZATION.searchPriorAuthorization);
    setter(value);
  } else {
    setter(undefined);
  }
}, 600);

export const PriorAuthorizationListPage: FC = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigateClient();
  const [pageNumber, setPageNumber] = useState(1);
  const [searchValue, setSearchValue] = useState(undefined);
  const [fullTextSearch, setFullTextSearch] = useState(undefined);
  const [outcomeStatus, setOutcomeStatus] = useState<string>(null);

  const { memberEligibility } = useContext(MemberEligibilityContext);

  const [personCode, setPersonCode] = useState<string>(memberEligibility.member.person_code);
  const { priorAuthorizationMessage } = useContext(ExperienceContext);

  const SEARCH_STATUS_OPTIONS = [
    {
      value: "approved",
      text: translate(`${TRANSLATION_PATH}.approved`),
    },
    {
      value: "denied",
      text: translate(`${TRANSLATION_PATH}.denied`),
    },
    {
      value: "closed",
      text: translate(`${TRANSLATION_PATH}.closed`),
    },
    {
      value: "in-progress",
      text: translate(`${TRANSLATION_PATH}.inProgress`),
    },
  ];

  const priorAuthorizationListFilters = useMemo(() => {
    return {
      page_number: pageNumber,
      results_per_page: RESULTS_PER_PAGE,
      text_search: fullTextSearch,
      case_outcome: outcomeStatus,
      person_code: personCode,
    };
  }, [pageNumber, fullTextSearch, outcomeStatus, personCode]);

  const {
    data: priorAuthorizationCases,
    isLoading: isLoadingPriorAuthorization,
    isRefetching: isRefetchingPriorAuthorization,
    refetch: refetchPriorAuthorizationCases,
  } = useListPriorAuthorizationCaseQuery(priorAuthorizationListFilters);

  const { data: dependents } = useListMemberDependentsQuery();

  const minorDependents = useMemo(() => {
    return (dependents?.results || []).filter((dependent) => dependent.member?.is_minor_dependent);
  }, [dependents]);

  const setSearchParams = (value: string) => {
    setFullTextSearch(value);
    setPageNumber(1); // Restarts paging on search
  };

  const setOutcomeStatusFilterCriteria = (value: string) => {
    setOutcomeStatus(value);
    setPageNumber(1); // Restarts paging outcome status filtering
  };

  useEffect(() => {
    changeSearch(setSearchParams, searchValue);
  }, [searchValue]);

  /**
   * Handle the search changing. Since this is a debounced search,
   * it doesn't happen on the regular render cycle.
   */
  useEffect(() => {
    refetchPriorAuthorizationCases();
  }, [fullTextSearch, outcomeStatus, personCode]);

  const { isCompressed, listColumns } = useResponsive({
    isCompressed: {
      mobile: true,
      desktop: false,
    },
    listColumns: {
      mobile: 12,
      tablet: 6,
      desktop: 6,
    },
  });

  const onPriorAuthorizationCaseView = useCallback((priorAuthorizationCase: CaseListResult) => {
    logEvent(ANALYTIC_EVENTS.PRIOR_AUTHORIZATION.viewCase);
    navigate(`/prior-authorization/${priorAuthorizationCase.case.case_id}`);
  }, []);

  const handleDependentFilterChange = (value: string) => {
    setPageNumber(1);
    setPersonCode(value);
  };

  return (
    <PageWrapper>
      <ContentPanel
        title={
          <Stack>
            <Shelf>
              <Stack>
                <Subtitle>{translate(`${TRANSLATION_PATH}.priorAuthorization`)}</Subtitle>
                {priorAuthorizationMessage && <Paragraph testId="prior-authorization-message">{priorAuthorizationMessage}</Paragraph>}
              </Stack>
              <Shelf.Spacer />
            </Shelf>
          </Stack>
        }
        showBorder={true}
      >
        <Stack spacing="large">
          <Grid>
            <Grid.Span columns={12}>
              {minorDependents.length > 0 && (
                <Shelf>
                  <DependentDropdown
                    testId="case-dependents-view-dropdown"
                    onChange={handleDependentFilterChange}
                    value={personCode}
                    label={translate(`${TRANSLATION_PATH}.caseDependentsViewDropdownLabel`)}
                    readOnly={(dependents?.results || []).length === 0}
                    dependents={minorDependents}
                    member={memberEligibility.member}
                  />
                  <Shelf.Spacer />
                </Shelf>
              )}
            </Grid.Span>
            <Grid.Span columns={isCompressed ? 12 : 9}>
              <Textbox
                prefix={<Icon name="search" />}
                testId="search-prior-authorization-textbox"
                value={searchValue}
                onChange={setSearchValue}
                label={translate(`${TRANSLATION_PATH}.search`)}
                placeholder={translate(`${TRANSLATION_PATH}.searchForDrug`)}
              />
            </Grid.Span>
            <Grid.Span columns={isCompressed ? 12 : 3}>
              <Dropdown
                testId="prior-authorization-list-view-dropdown"
                onChange={(value) => setOutcomeStatusFilterCriteria(value?.toString())}
                value={outcomeStatus}
                clearable={true}
                label={translate(`${TRANSLATION_PATH}.status`)}
                placeholder={translate(`${TRANSLATION_PATH}.select`)}
              >
                {SEARCH_STATUS_OPTIONS.map((status_option) => {
                  return (
                    <Dropdown.Option value={status_option.value} key={status_option.value}>
                      {status_option.text}
                    </Dropdown.Option>
                  );
                })}
              </Dropdown>
            </Grid.Span>
          </Grid>
          <LoadingGroup loading={isLoadingPriorAuthorization || isRefetchingPriorAuthorization}>
            <LoadingGroup.Placeholder
              fallback={
                <Grid spacing="small">
                  <Grid.Span columns={listColumns}>
                    <LoadingSkeleton style={{ height: 200 }} />
                  </Grid.Span>
                  <Grid.Span columns={listColumns}>
                    <LoadingSkeleton style={{ height: 200 }} />
                  </Grid.Span>
                  <Grid.Span columns={listColumns}>
                    <LoadingSkeleton style={{ height: 200 }} />
                  </Grid.Span>
                  <Grid.Span columns={listColumns}>
                    <LoadingSkeleton style={{ height: 200 }} />
                  </Grid.Span>
                </Grid>
              }
            />
            {!isLoadingPriorAuthorization && !isRefetchingPriorAuthorization && (
              <>
                <Grid>
                  {priorAuthorizationCases?.results.map((priorAuthorizationCase) => {
                    return (
                      <Grid.Span columns={listColumns} key={`grid-${priorAuthorizationCase.case.case_id}`}>
                        <PriorAuthorizationCard
                          key={priorAuthorizationCase.case.case_id}
                          onPriorAuthorizationCaseView={onPriorAuthorizationCaseView}
                          priorAuthorizationCase={priorAuthorizationCase}
                        />
                      </Grid.Span>
                    );
                  })}
                </Grid>
                {(priorAuthorizationCases?.results || []).length === 0 && (
                  <NoResults message={translate(`${TRANSLATION_PATH}.thereAreNoPriorAuthorizations`)} />
                )}
                {(priorAuthorizationCases?.results || []).length > 0 && (
                  <Pager
                    pageNumber={pageNumber}
                    setPageNumber={setPageNumber}
                    resultCount={priorAuthorizationCases?.result_count}
                    resultsPerPage={RESULTS_PER_PAGE}
                    disabled={isLoadingPriorAuthorization || isRefetchingPriorAuthorization}
                    analyticsTag={ANALYTIC_EVENTS.PRIOR_AUTHORIZATION.pagination.pageNumber}
                  />
                )}
              </>
            )}
          </LoadingGroup>
        </Stack>
      </ContentPanel>
    </PageWrapper>
  );
};
